import { Box, Button, CircularProgress, Link, Typography } from "@material-ui/core";
import CloudDownload from "@material-ui/icons/CloudDownload";
import { ChangeEvent, useEffect, useRef, useState } from "react";

import CustomCard from "../../../../components/CustomCard";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import { GenericObject } from "../../../../types/generic.types";
import { AppResource } from "../../../../types/resource.types";
import { handleLocalFileEntries, handleUploadFilesToStorage } from "../../../../utils/fileUpload";
import { productsFilesCollRef } from "../../../../utils/referencies";
import { applyCreateTimestamp } from "../../../../utils/timestamp";
import { IFileStatus } from "../../types";

const BulkProductsCreate = () => {
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = useState<GenericObject>({});
  const [loading, setLoading] = useState(false);

  const { user } = useCurrentUser();
  const inputRef = useRef<any>();

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const localFilesEntries = handleLocalFileEntries(e.target.files, `${AppResource.productsFiles}/${user.id}`);
    setSelectedFiles(localFilesEntries);
  };

  const handleUploadSvcFile = async () => {
    try {
      setLoading(true);
      const uploadedFilesData = await handleUploadFilesToStorage(selectedFiles);
      if (uploadedFilesData.length)
        await productsFilesCollRef.doc().set(
          applyCreateTimestamp({
            url: uploadedFilesData[0].src,
            brandId: user.id,
            status: "pending",
          })
        );
      setLoading(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoading(false);
    } finally {
      inputRef.current.value = "";
      setSelectedFiles([]);
    }
  };

  const isLoadingBtn = () => loading || uploadedFile.status === IFileStatus.pending;
  const isDisabledBtn = () => isLoadingBtn() || selectedFiles.length === 0;
  const isValidFileType = (entry: any) => (entry.length ? entry[0].name.endsWith(".csv") : true);

  useEffect(() => {
    const unsubscribeToFile = productsFilesCollRef
      .where("brandId", "==", user.id)
      .orderBy("createdAt", "desc")
      .onSnapshot({
        next(snapshot) {
          if (!snapshot.empty) {
            const file = snapshot.docs[0].data();
            setUploadedFile(file);
          }
        },
        error(ex) {
          console.log(ex.message);
        },
      });

    return () => {
      unsubscribeToFile();
    };
  });

  return (
    <CustomCard width="50%" title="CSV File Uploader">
      <Link
        href="https://firebasestorage.googleapis.com/v0/b/sporting-morecreative-net.appspot.com/o/productsFiles%2Fproductscsv%20template.csv?alt=media&token=571a2d7f-0c27-4437-8f2f-fb495ec955ef"
        download
      >
        <Box display="flex" alignItems="center">
          <CloudDownload style={{ color: "#74BD76" }} />
          <Typography variant="body2" style={{ marginInline: 8 }}>
            Download CSV File Template
          </Typography>
        </Box>
      </Link>

      <Box mt={3}>
        <Box>
          <label htmlFor="csv-file-upload">Upload CSV file</label>
          <input
            type="file"
            id="csv-file-upload"
            accept="text/csv"
            onChange={handleFileChange}
            style={{ marginInline: 4 }}
            disabled={isLoadingBtn()}
            ref={inputRef}
          />
        </Box>

        <Box display="flex" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUploadSvcFile}
            startIcon={isLoadingBtn() ? <CircularProgress size={12} /> : null}
            disabled={isDisabledBtn() || !isValidFileType(selectedFiles)}
          >
            Upload
          </Button>
        </Box>

        {!isValidFileType(selectedFiles) && (
          <Typography color="error" variant="body2">
            Only accept CSVs files
          </Typography>
        )}

        <Box mt={1}>
          <Typography variant="h6" color="textSecondary">
            Latest uploaded file status
          </Typography>

          {uploadedFile.status === IFileStatus.pending ? (
            <Typography color="textSecondary" variant="body2">
              Processing...
            </Typography>
          ) : null}
          {uploadedFile.status === IFileStatus.error && uploadedFile.message ? (
            <Typography color="error" variant="body2">
              {`${uploadedFile.message[0].toUpperCase()}${uploadedFile.message.slice(1)}`}
            </Typography>
          ) : null}
          {uploadedFile.status === IFileStatus.success && uploadedFile.message ? (
            <Typography color="primary" variant="body2">
              File has been uploaded successfully
            </Typography>
          ) : null}
        </Box>
      </Box>
    </CustomCard>
  );
};

export default BulkProductsCreate;
